import { assertUnreachable, isNullish } from '@payaca/utilities/guards';
import { getReadableStringFromDurationString } from '@payaca/utilities/timeUtilities';
import { ValidateFunction } from 'ajv';
import { ajv } from './ajv';
import { MAX_FIELDSET_DEPTH } from './constants';
import { EqualityOptionsByType, stringEquals } from './equality';
import {
  CustomFieldDefinitionInput,
  CustomFieldHelperType,
  CustomFieldType,
  FieldsetCustomFieldDefinitionInput,
  Schema,
  SelectCustomFieldDefinitionInput,
  SimpleCustomFieldDefinitionInput,
  UploadCustomFieldDefinitionInput,
} from './types';
export * from './equality';
export * from './type-guards';
export * from './types';

/** Union of all concrete custom field definition types */
export type AnyCustomFieldDefinition =
  | BooleanCustomFieldDefinition
  | DurationCustomFieldDefinition
  | EmailCustomFieldDefinition
  | FieldsetCustomFieldDefinition
  | MultiUploadCustomFieldDefinition
  | NumberCustomFieldDefinition
  | SelectCustomFieldDefinition
  | TextareaCustomFieldDefinition
  | TextCustomFieldDefinition
  | UploadCustomFieldDefinition
  | UrlCustomFieldDefinition
  | UserCustomFieldDefinition;

export type ChildWithPath = {
  definition: AnyCustomFieldDefinition;
  /** Full path segments including root fieldset identifier, e.g., ['rootFieldset', 'childField'] or ['rootFieldset', 'nestedFieldset', 'childField'] */
  path: string[];
};

export const CUSTOM_FIELD_TYPE_READABLE_NAME_MAP: Record<
  CustomFieldType,
  string
> = {
  textarea: 'Textarea',
  duration: 'Duration',
  'multi-upload': 'Multiple file upload',
  text: 'Text',
  email: 'Email',
  select: 'Select',
  boolean: 'True/false',
  fieldset: 'Fieldset',
  number: 'Number',
  url: 'URL',
  upload: 'Image upload',
  user: 'User',
};

export abstract class CustomFieldDefinition<
  T extends CustomFieldType = CustomFieldType,
> {
  static fromInput(input: CustomFieldDefinitionInput) {
    switch (input.type) {
      case 'text':
        return TextCustomFieldDefinition.fromInput(input);
      case 'textarea':
        return TextareaCustomFieldDefinition.fromInput(input);
      case 'email':
        return EmailCustomFieldDefinition.fromInput(input);
      case 'select':
        return SelectCustomFieldDefinition.fromInput(input);
      case 'duration':
        return DurationCustomFieldDefinition.fromInput(input);
      case 'multi-upload':
        return MultiUploadCustomFieldDefinition.fromInput(input);
      case 'boolean':
        return BooleanCustomFieldDefinition.fromInput(input);
      case 'number':
        return NumberCustomFieldDefinition.fromInput(input);
      case 'url':
        return UrlCustomFieldDefinition.fromInput(input);
      case 'fieldset':
        return FieldsetCustomFieldDefinition.fromInput(input);
      case 'upload':
        return UploadCustomFieldDefinition.fromInput(input);
      case 'user':
        return UserCustomFieldDefinition.fromInput(input);
      default:
        throw new Error('Unreachable');
    }
  }

  static fromSchema(schema: Schema) {
    switch (schema.metadata['payaca:field-type']) {
      case 'text':
        return new TextCustomFieldDefinition(schema);
      case 'textarea':
        return new TextareaCustomFieldDefinition(schema);
      case 'email':
        return new EmailCustomFieldDefinition(schema);
      case 'select':
        return new SelectCustomFieldDefinition(schema);
      case 'duration':
        return new DurationCustomFieldDefinition(schema);
      case 'multi-upload':
        return new MultiUploadCustomFieldDefinition(schema);
      case 'boolean':
        return new BooleanCustomFieldDefinition(schema);
      case 'number':
        return new NumberCustomFieldDefinition(schema);
      case 'url':
        return new UrlCustomFieldDefinition(schema);
      case 'fieldset':
        return new FieldsetCustomFieldDefinition(schema);
      case 'upload':
        return new UploadCustomFieldDefinition(schema);
      case 'user':
        return new UserCustomFieldDefinition(schema);
      default:
        assertUnreachable(schema.metadata['payaca:field-type']);
    }

    throw new Error('Unreachable');
  }

  readonly schema: Schema;
  readonly type: T;
  protected validator: ValidateFunction | null;
  protected formValidator: ValidateFunction | null;

  constructor(schema: Schema) {
    this.schema = schema;
    this.type = schema.metadata['payaca:field-type'] as T;
    // Validator for the value alone of this field
    this.validator = null;
    // Validator for a form containing only this field by its identifier
    this.formValidator = null;
  }

  abstract getExampleValue(): CustomFieldHelperType[T]['value'];

  /**
   * Strict-by-default equality between two values of this field's type.
   * `null === null` is true; `null` versus any other value (including `""`)
   * is false. `text`, `textarea`, and `email` accept `{ caseSensitive?, trim? }`.
   * Throws on `url`, `upload`, `multi-upload`, and `fieldset` — semantics for
   * compound types will land alongside the first concrete caller.
   */
  abstract equals(
    a: CustomFieldHelperType[T]['value'] | null,
    b: CustomFieldHelperType[T]['value'] | null,
    options?: EqualityOptionsByType[T]
  ): boolean;

  protected static getSharedSchemaPropertiesFromInput(
    input: CustomFieldDefinitionInput
  ) {
    return {
      metadata: {
        ['payaca:label']: input.label,
        ['payaca:field-type']: input.type,
        ['payaca:identifier']: input.identifier,
        ['payaca:description']: input.description,
      },
      ['$schema']: 'http://json-schema.org/draft/2020-12/schema',
    };
  }

  public validateValue(value: any): {
    isValid: boolean;
    validationMessages: string[];
  } {
    if (!this.validator) {
      const { $schema, ...schema } = this.schema;
      this.validator = ajv.compile(schema);
    }
    const isValid = this.validator(value);

    return {
      isValid,
      validationMessages:
        this.validator.errors?.map((error) => error.message || '') ?? [],
    };
  }

  typecheckValue(value: any): value is CustomFieldHelperType[T]['value'] {
    return this.validateValue(value).isValid;
  }

  protected abstract defaultFormatter(
    value: CustomFieldHelperType[T]['value']
  ): string;

  formatValue(
    value: unknown,
    customFormatter?: (value: CustomFieldHelperType[T]['value']) => string
  ): string | null {
    if (this.typecheckValue(value)) {
      if (value === null) return null;
      if (customFormatter) {
        return customFormatter(value);
      } else {
        return this.defaultFormatter(value);
      }
    } else {
      return null;
    }
  }

  get label(): string {
    return this.schema?.metadata?.['payaca:label'];
  }

  set label(value: string) {
    this.schema.metadata['payaca:label'] = value;
  }

  get identifier(): string {
    return this.schema?.metadata?.['payaca:identifier'];
  }

  get sortIndex(): number | undefined {
    return this.schema?.metadata?.['payaca:sort-index'];
  }

  get description(): string | undefined {
    return this.schema?.metadata?.['payaca:description'];
  }

  set description(value: string | undefined) {
    this.schema.metadata['payaca:description'] = value;
  }

  public get isRequired(): boolean {
    return !(
      Array.isArray(this.schema.type) && this.schema.type.includes('null')
    );
  }

  public set isRequired(value: boolean) {
    if (value) {
      if (Array.isArray(this.schema.type)) {
        this.schema.type = this.schema.type.filter((x) => x !== 'null');
      }
    } else {
      if (Array.isArray(this.schema.type)) {
        this.schema.type = ['null', ...this.schema.type];
      } else {
        this.schema.type = ['null', this.schema.type];
      }

      this.schema.type = ['null'];
    }
  }
}
export class TextCustomFieldDefinition extends CustomFieldDefinition<'text'> {
  getExampleValue() {
    return 'Lorem Ipsum';
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'text') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'string' : ['string', 'null'],
    };

    return new TextCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['text']['value']) {
    return value;
  }

  equals(
    a: CustomFieldHelperType['text']['value'] | null,
    b: CustomFieldHelperType['text']['value'] | null,
    options?: EqualityOptionsByType['text']
  ): boolean {
    return stringEquals(a, b, options);
  }
}

export class TextareaCustomFieldDefinition extends CustomFieldDefinition<'textarea'> {
  getExampleValue() {
    return 'Lorem Ipsum';
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'textarea') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'string' : ['string', 'null'],
    };

    return new TextareaCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['textarea']['value']) {
    return value;
  }

  equals(
    a: CustomFieldHelperType['textarea']['value'] | null,
    b: CustomFieldHelperType['textarea']['value'] | null,
    options?: EqualityOptionsByType['textarea']
  ): boolean {
    return stringEquals(a, b, options);
  }
}

export class EmailCustomFieldDefinition extends CustomFieldDefinition<'email'> {
  getExampleValue() {
    return 'example@email.com';
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'email') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'string' : ['string', 'null'],
      format: 'email',
    };

    return new EmailCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['email']['value']) {
    return value;
  }

  equals(
    a: CustomFieldHelperType['email']['value'] | null,
    b: CustomFieldHelperType['email']['value'] | null,
    options?: EqualityOptionsByType['email']
  ): boolean {
    return stringEquals(a, b, options);
  }
}

export class SelectCustomFieldDefinition extends CustomFieldDefinition<'select'> {
  getExampleValue() {
    return this.options.filter((x) => x)[0] || 'selectable option';
  }

  static fromInput(input: SelectCustomFieldDefinitionInput) {
    if (input.type !== 'select') throw new Error('Invalid type');

    const options: (string | null)[] | undefined = input.options;

    if (options && !input.isRequired) {
      options.push(null);
    }

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'string' : ['string', 'null'],
      enum: options,
    };

    if (input.displayMode) {
      schema.metadata['payaca:display-mode'] = input.displayMode;
    }

    return new SelectCustomFieldDefinition(schema);
  }

  get displayMode(): 'dropdown' | 'radio' {
    return this.schema.metadata['payaca:display-mode'] ?? 'dropdown';
  }

  set displayMode(value: 'dropdown' | 'radio') {
    this.schema.metadata['payaca:display-mode'] = value;
  }

  get options(): (string | null)[] {
    return this.schema.enum as (string | null)[];
  }

  set options(value: string[]) {
    if (this.isRequired) {
      this.schema.enum = value;
    } else {
      this.schema.enum = [null, ...value];
    }
  }

  set isRequired(value: boolean) {
    super.isRequired = value;

    if (value) {
      this.schema.enum = this.schema.enum?.filter((x) => x !== null);
    } else {
      this.schema.enum = [null, ...(this.schema.enum || [])];
    }
  }

  defaultFormatter(value: CustomFieldHelperType['select']['value']) {
    return value;
  }

  equals(
    a: CustomFieldHelperType['select']['value'] | null,
    b: CustomFieldHelperType['select']['value'] | null
  ): boolean {
    return a === b;
  }
}

export class DurationCustomFieldDefinition extends CustomFieldDefinition<'duration'> {
  getExampleValue(): CustomFieldHelperType['duration']['value'] {
    return 'P0Y0M0DT1H0M0S';
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'duration') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'string' : ['string', 'null'],
    };

    return new DurationCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['duration']['value']) {
    if (!value) return '';
    const readableDuration = getReadableStringFromDurationString(value, {});
    return readableDuration;
  }

  equals(
    a: CustomFieldHelperType['duration']['value'] | null,
    b: CustomFieldHelperType['duration']['value'] | null
  ): boolean {
    return a === b;
  }
}

export class MultiUploadCustomFieldDefinition extends CustomFieldDefinition<'multi-upload'> {
  getExampleValue() {
    return {
      uploadIds: ['abc123'],
      uploadIntentPublicIds: [],
    };
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'multi-upload') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'object' : ['object', 'null'],
      properties: {
        uploadIds: {
          type: 'array',
          items: {
            type: 'string',
          },
        },
        uploadIntentPublicIds: {
          type: 'array',
          items: {
            type: 'string',
          },
        },
      },
    };

    return new MultiUploadCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['multi-upload']['value']) {
    const count = value.uploadIds.length + value.uploadIntentPublicIds.length;
    return `${count} files uploaded`;
  }

  equals(
    _a: CustomFieldHelperType['multi-upload']['value'] | null,
    _b: CustomFieldHelperType['multi-upload']['value'] | null
  ): boolean {
    throw new Error(
      'MultiUploadCustomFieldDefinition#equals is not implemented; multi-upload equality semantics will land alongside the first concrete caller'
    );
  }
}

export class BooleanCustomFieldDefinition extends CustomFieldDefinition<'boolean'> {
  getExampleValue(): CustomFieldHelperType['boolean']['value'] {
    return true;
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'boolean') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'boolean' : ['boolean', 'null'],
    };

    return new BooleanCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['boolean']['value']) {
    return value ? 'True' : 'False';
  }

  equals(
    a: CustomFieldHelperType['boolean']['value'] | null,
    b: CustomFieldHelperType['boolean']['value'] | null
  ): boolean {
    return a === b;
  }
}

export class NumberCustomFieldDefinition extends CustomFieldDefinition<'number'> {
  getExampleValue() {
    return 100;
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'number') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'number' : ['number', 'null'],
    };

    return new NumberCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['number']['value']) {
    return value.toString();
  }

  equals(
    a: CustomFieldHelperType['number']['value'] | null,
    b: CustomFieldHelperType['number']['value'] | null
  ): boolean {
    return a === b;
  }
}

export class UrlCustomFieldDefinition extends CustomFieldDefinition<'url'> {
  getExampleValue() {
    return {
      title: 'Payaca',
      url: 'https://payaca.com/',
    };
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'url') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'object' : ['object', 'null'],
      properties: {
        title: {
          type: ['string', 'null'],
        },
        url: {
          type: 'string',
          minLength: 1,
        },
      },
      required: ['url'],
    };

    return new UrlCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['url']['value']) {
    if (value.title?.length) {
      return `${value.title} (${value.url})`;
    }

    return value.url;
  }

  validateValue(value: any) {
    const res = super.validateValue(value);

    if (!res.isValid) {
      return res;
    }

    if (value === null) {
      return {
        isValid: true,
        validationMessages: [],
      };
    }

    if (value.url) {
      try {
        new URL(value.url);
      } catch (e) {
        return {
          isValid: false,
          validationMessages: ['Invalid URL'],
        };
      }
    }

    return {
      isValid: true,
      validationMessages: [],
    };
  }

  equals(
    _a: CustomFieldHelperType['url']['value'] | null,
    _b: CustomFieldHelperType['url']['value'] | null
  ): boolean {
    throw new Error(
      'UrlCustomFieldDefinition#equals is not implemented; URL equality semantics will land alongside the first concrete caller'
    );
  }
}

// Sort children by sort-order; children with undefined sort-order values come last
export const sortBySortOrder = (a: Schema, b: Schema) => {
  if (a.metadata['payaca:sort-index'] === undefined) {
    return 1;
  }
  if (b.metadata['payaca:sort-index'] === undefined) {
    return -1;
  }
  return a.metadata['payaca:sort-index'] < b.metadata['payaca:sort-index']
    ? -1
    : 1;
};

export class FieldsetCustomFieldDefinition extends CustomFieldDefinition<'fieldset'> {
  getExampleValue(): CustomFieldHelperType['fieldset']['value'] {
    const result: CustomFieldHelperType['fieldset']['value'] = {};
    this.children.forEach((child) => {
      // Type assertion needed because nested fieldsets return their own value type
      // which is structurally compatible but TypeScript can't verify the depth constraint
      result[child.identifier] =
        child.getExampleValue() as CustomFieldHelperType['fieldset']['value'][string];
    });

    return result;
  }

  constructor(schema: Schema) {
    // If provided a schema non-contiguous or undefined sort orders on children, set them to be contiguous
    const childSchemas: Schema[] = Object.values(
      schema?.properties || {}
    ).filter((x) => x.metadata && x.type) as Schema[];

    const sortedChildSchemas = childSchemas.sort(sortBySortOrder);

    const sortedProperties: Record<string, Schema> = {};

    for (const [index, childSchema] of sortedChildSchemas.entries()) {
      if (childSchema.metadata && childSchema.type) {
        const identifier = childSchema.metadata?.['payaca:identifier'];

        sortedProperties[identifier] = {
          ...childSchema,
          metadata: {
            ...childSchema.metadata,
            ['payaca:sort-index']: index + 1,
          },
        };
      }
    }

    super({
      ...schema,
      properties: {
        ...schema.properties,
        ...sortedProperties,
      },
    });
  }

  static fromInput(input: FieldsetCustomFieldDefinitionInput, depth = 1) {
    if (input.type !== 'fieldset') throw new Error('Invalid type');
    if (depth > MAX_FIELDSET_DEPTH) {
      throw new Error(
        `Fieldsets cannot be nested more than ${MAX_FIELDSET_DEPTH} levels deep`
      );
    }

    const properties: Record<string, Schema> = {};

    for (const [index, child] of input.children.entries() || []) {
      let childSchema: Schema;
      if (child.type === 'fieldset') {
        childSchema = FieldsetCustomFieldDefinition.fromInput(
          child,
          depth + 1
        ).schema;
      } else {
        childSchema = CustomFieldDefinition.fromInput(child).schema;
      }
      childSchema.metadata['payaca:sort-index'] = index + 1;

      properties[child.identifier] = childSchema;
    }

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'object' : ['object', 'null'],
      properties,
      additionalProperties: false,
    };

    return new FieldsetCustomFieldDefinition(schema);
  }

  get children(): AnyCustomFieldDefinition[] {
    const children = Object.values(
      this.schema.properties as Record<string, Schema>
    );
    const keys = Object.keys(this.schema.properties as Record<string, Schema>);

    if (!children?.length)
      throw new Error('Children are required for fieldset');

    const duplicateKeys = keys.filter(
      (item, index) => keys.indexOf(item) !== index
    );

    if (duplicateKeys.length) {
      throw new Error(
        `Duplicate keys found in fieldset: ${duplicateKeys.join(', ')}`
      );
    }

    return children
      .sort(sortBySortOrder)
      .map((child) => CustomFieldDefinition.fromSchema(child));
  }

  /**
   * Returns all children with their full paths including this fieldset's identifier.
   * For nested fieldsets, recursively includes their children.
   * Paths always start with this fieldset's identifier.
   *
   * @example
   * // For a fieldset with identifier 'projectDetails' and child 'name':
   * // Returns: [{ definition, path: ['projectDetails', 'name'] }]
   *
   * // For nested: 'projectDetails' -> 'address' (fieldset) -> 'street':
   * // Returns paths like ['projectDetails', 'address'] and ['projectDetails', 'address', 'street']
   */
  getChildrenWithPaths(): ChildWithPath[] {
    const rootPath = [this.identifier];

    const collectChildren = (
      children: AnyCustomFieldDefinition[],
      parentPath: string[]
    ): ChildWithPath[] => {
      return children.flatMap((child) => {
        const childPath = [...parentPath, child.identifier];
        const result: ChildWithPath[] = [
          {
            definition: child,
            path: childPath,
          },
        ];

        // Recurse into nested fieldsets
        if (child instanceof FieldsetCustomFieldDefinition) {
          result.push(...collectChildren(child.children, childPath));
        }

        return result;
      });
    };

    return collectChildren(this.children, rootPath);
  }

  /**
   * Finds a child by its full path segments (including root fieldset identifier).
   * @param path - Full path segments, e.g., ['rootFieldset', 'childField'] or ['rootFieldset', 'nestedFieldset', 'childField']
   */
  findChildByPath(path: string[]): AnyCustomFieldDefinition | undefined {
    return this.getChildrenWithPaths().find(
      (c) =>
        c.path.length === path.length && c.path.every((p, i) => p === path[i])
    )?.definition;
  }

  defaultFormatter(value: CustomFieldHelperType['fieldset']['value']) {
    return JSON.stringify(value);
  }

  equals(
    _a: CustomFieldHelperType['fieldset']['value'] | null,
    _b: CustomFieldHelperType['fieldset']['value'] | null
  ): boolean {
    // Whole-fieldset equality is not defined: each child has its own equals
    // semantics (e.g. string `caseSensitive`/`trim`) that a structural compare
    // would silently ignore. Callers must resolve to a leaf and compare there.
    throw new Error(
      'FieldsetCustomFieldDefinition#equals is not implemented; resolve to a leaf field and compare there'
    );
  }
}

export const IMAGE_EXTENSIONS = [
  '.png',
  '.jpeg',
  '.jpg',
  '.webp',
  '.heic',
] as const;
export const DOCUMENT_EXTENSIONS = ['.doc', '.docx', '.pdf'] as const;
export const VIDEO_EXTENSIONS = ['.mp4', '.mov'] as const;

type ImageExtensions = (typeof IMAGE_EXTENSIONS)[number];
type DocumentExtensions = (typeof DOCUMENT_EXTENSIONS)[number];
type VideoExtensions = (typeof VIDEO_EXTENSIONS)[number];

type UploadExtensions = ImageExtensions | DocumentExtensions | VideoExtensions;

export class UploadCustomFieldDefinition extends CustomFieldDefinition<'upload'> {
  getExampleValue() {
    return [
      {
        name: 'cat.jpg',
        mimeType: 'image/jpeg',
        // Not super realistic but will actually resolve to an image so
        // good for dummy data purposes
        url: 'https://placecats.com/900/600',
        thumbnailUrl: 'https://placecats.com/300/200',
      },
    ];
  }

  static fromInput(input: UploadCustomFieldDefinitionInput) {
    if (input.type !== 'upload') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'array' : ['array', 'null'],
      items: {
        type: 'object',
        properties: {
          url: {
            type: 'string',
          },
          name: {
            type: 'string',
          },
          mimeType: {
            type: 'string',
          },
          thumbnailUrl: {
            type: ['string', 'null'],
          },
        },
      },
      maxItems: input.limit || 1,
      minItems: input.isRequired ? 1 : 0,
    };

    const acceptedFileExtensions: UploadExtensions[] = [];

    if (input.uploadType?.includes('image')) {
      acceptedFileExtensions.push(...IMAGE_EXTENSIONS);
    }

    if (input.uploadType?.includes('document')) {
      acceptedFileExtensions.push(...DOCUMENT_EXTENSIONS);
    }

    if (input.uploadType?.includes('video')) {
      acceptedFileExtensions.push(...VIDEO_EXTENSIONS);
    }

    if (!input.uploadType?.length) {
      acceptedFileExtensions.push(...IMAGE_EXTENSIONS);
    }

    schema.metadata['payaca:accepted-file-extensions'] = acceptedFileExtensions;

    return new UploadCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['upload']['value']) {
    if (value.length > 0) {
      return `${value.length} file(s) uploaded: ${value
        .map((u) => u.url)
        .join(', ')}`;
    }

    return `0 files uploaded`;
  }

  get limit(): number {
    const limit = this.schema?.maxItems;

    return isNullish(limit) ? 1 : (limit as number);
  }

  get uploadTypes(): ('image' | 'document' | 'video')[] {
    const acceptedFileExtensions = (this.schema?.metadata?.[
      'payaca:accepted-file-extensions'
    ] || []) as UploadExtensions[];

    const uploadTypes: ('image' | 'document' | 'video')[] = [];

    if (
      acceptedFileExtensions.some((ext) =>
        IMAGE_EXTENSIONS.includes(ext as ImageExtensions)
      )
    ) {
      uploadTypes.push('image');
    }

    if (
      acceptedFileExtensions.some((ext) =>
        DOCUMENT_EXTENSIONS.includes(ext as DocumentExtensions)
      )
    ) {
      uploadTypes.push('document');
    }

    if (
      acceptedFileExtensions.some((ext) =>
        VIDEO_EXTENSIONS.includes(ext as VideoExtensions)
      )
    ) {
      uploadTypes.push('video');
    }

    return uploadTypes;
  }

  get acceptedFileExtensions(): UploadExtensions[] {
    const acceptedFileExtensions =
      this.schema?.metadata?.['payaca:accepted-file-extensions'];

    return isNullish(acceptedFileExtensions)
      ? []
      : (acceptedFileExtensions as UploadExtensions[]);
  }

  equals(
    _a: CustomFieldHelperType['upload']['value'] | null,
    _b: CustomFieldHelperType['upload']['value'] | null
  ): boolean {
    throw new Error(
      'UploadCustomFieldDefinition#equals is not implemented; upload equality semantics will land alongside the first concrete caller'
    );
  }
}

export class UserCustomFieldDefinition extends CustomFieldDefinition<'user'> {
  getExampleValue() {
    return {
      userId: 'user_123',
    };
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'user') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'object' : ['object', 'null'],
      properties: {
        userId: {
          type: ['string'],
        },
      },
    };

    return new UserCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['user']['value']) {
    if (value?.userId?.length) {
      return `User ${value.userId}`;
    }
    return '';
  }

  equals(
    a: CustomFieldHelperType['user']['value'] | null,
    b: CustomFieldHelperType['user']['value'] | null
  ): boolean {
    if (a === null || b === null) return a === b;
    return a.userId === b.userId;
  }
}
